import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Table, Form, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Componente para mostrar el resumen de totales de la orden
 */
const ResumenTotales = ({
    orden,
    showEditarPrecios,
    editedProducts,
    editedMoneda,
    setEditedMoneda,
    inicializarEdicion,
    guardarCambiosPrecios,
    cancelarEdicion,
    guardandoCambios
}) => {
    return (
        <Row className="g-0 justify-content-end">
            <Col xs="auto">
                <Table borderless size="sm" className="fs--1 text-end">
                    <tbody>
                        {/* Subtotal */}
                        <tr>
                            <th className="text-900">Subtotal:</th>
                            <td className="fw-semi-bold">
                                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                                    <span className="text-primary fw-bold">
                                        ${Object.values(editedProducts).reduce((sum, product) => sum + (product.dImporte || 0), 0).toLocaleString("es-MX", {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2
                                        })}
                                    </span>
                                ) : (
                                    parseFloat(orden.dSubtotal).toLocaleString("es-MX", {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                        style: 'currency',
                                        currency: 'MXN'
                                    })
                                )}
                            </td>
                        </tr>

                        {/* Descuento */}
                        <tr>
                            <th className="text-900">Descuento:</th>
                            <td className="fw-semi-bold">
                                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                                    <span className="text-primary fw-bold">
                                        ${Object.values(editedProducts).reduce((sum, product) => sum + (product.dDescuento || 0), 0).toLocaleString("es-MX", {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2
                                        })}
                                    </span>
                                ) : (
                                    '$' + parseFloat(orden.dDescuento).toLocaleString("es-MX", {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2
                                    })
                                )}
                            </td>
                        </tr>

                        {/* IVA */}
                        <tr>
                            <th className="text-900">IVA 16%:</th>
                            <td className="fw-semi-bold">
                                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                                    <span className="text-primary fw-bold">
                                        ${Object.values(editedProducts).reduce((sum, product) => sum + (product.dIVA || 0), 0).toLocaleString("es-MX", {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2
                                        })}
                                    </span>
                                ) : (
                                    '$' + parseFloat(orden.dIVA).toLocaleString("es-MX", {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2
                                    })
                                )}
                            </td>
                        </tr>

                        {/* Total */}
                        <tr className="border-top">
                            <th className="text-900">Total:</th>
                            <td className="fw-semi-bold">
                                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                                    <div className="d-flex align-items-center justify-content-end">
                                        <Form.Select
                                            size="sm"
                                            value={editedMoneda}
                                            onChange={(e) => setEditedMoneda(e.target.value)}
                                            style={{ width: 'auto', marginRight: '8px' }}
                                        >
                                            <option value="MXN">MXN</option>
                                            <option value="USD">USD</option>
                                        </Form.Select>
                                        <span className="text-success fw-bold">
                                            ${Object.values(editedProducts).reduce((sum, product) => sum + (product.dImporte || 0) + (product.dIVA || 0), 0).toLocaleString("es-MX", {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2
                                            })}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {orden.vMoneda} &nbsp;
                                        {parseFloat(orden.dTotal).toLocaleString("es-MX", {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2,
                                            style: 'currency',
                                            currency: 'MXN'
                                        })}
                                    </>
                                )}

                                {!showEditarPrecios && orden.bSePuedeEditarPrecios && (
                                    <Link to={''} onClick={inicializarEdicion}>
                                        <img
                                            title="Editar Precios, Cantidades y Moneda"
                                            src="/img/editar20.png"
                                            className="ms-2"
                                            alt="Editar"
                                        />
                                    </Link>
                                )}
                            </td>
                        </tr>

                        {/* Botones de acción para edición */}
                        {showEditarPrecios && orden.bSePuedeEditarPrecios && (
                            <tr>
                                <th></th>
                                <td>
                                    <div className="d-flex flex-column gap-2">
                                        <div className="d-flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="success"
                                                className="d-flex align-items-center"
                                                onClick={guardarCambiosPrecios}
                                                disabled={guardandoCambios}
                                            >
                                                {guardandoCambios ? (
                                                    <>
                                                        <Spinner size="sm" className="me-1" />
                                                        Guardando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon="save" className="me-1" />
                                                        Guardar Cambios
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={cancelarEdicion}
                                                disabled={guardandoCambios}
                                            >
                                                Cancelar
                                            </Button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Col>
        </Row>
    );
};

ResumenTotales.propTypes = {
    orden: PropTypes.object.isRequired,
    showEditarPrecios: PropTypes.bool.isRequired,
    editedProducts: PropTypes.object.isRequired,
    editedMoneda: PropTypes.string.isRequired,
    setEditedMoneda: PropTypes.func.isRequired,
    inicializarEdicion: PropTypes.func.isRequired,
    guardarCambiosPrecios: PropTypes.func.isRequired,
    cancelarEdicion: PropTypes.func.isRequired,
    guardandoCambios: PropTypes.bool.isRequired
};

export default ResumenTotales;
